// minima.ts — Price floors (book REGLES_DEVIS §3).
//
// Two levels: material minimum unit price (floor IN EUROS PER PIECE, including the material + CMYK
// front + straight cutting base, cumulated when laminated) and material minimum flat fee (global
// floor amount, triggered when 0 < material total < flat fee, gone once the cumulation exceeds it).
// Final unit price = max(theoretical unit price, minimum unit price). A forced price (forcedPv) skips
// the protection; a forced minimum unit price (forcedMinUnitPrice) stays protected.
//
// Ported from compute() of prototype v13.7. Per-category aggregation stays in compute().

import { ACTIVE_CATALOGUE, type Catalogue } from "../catalogue/catalogue-source";
import type { MinUnitPrice } from "../../models/data.model";
import { type QuoteLine, type ForcedMaterial, num, isFilled, findMat, materialInFinishing } from "../core/core";

/** Component carrying a minimum unit price field (printing, cutting, lamination). */
interface WithMinUnitPrice {
  code: string;
  /** Optional: a finishing carries none, except CONF-1639 (lamination). */
  min_unit_price?: MinUnitPrice;
}

/**
 * Minimum unit price of a component (€/piece, outside the base). An empty field ("") counts as 0:
 * CMYK front printing and straight cutting are already in the material base and add nothing.
 * @param list The component list to search.
 * @param code The component code.
 * @returns The component minimum unit price, or 0.
 */
export function componentMinUnitPriceOf(list: readonly WithMinUnitPrice[], code: string | undefined): number {
  const c = list.find((x) => x.code === code);
  return c && isFilled(c.min_unit_price) ? num(c.min_unit_price) : 0;
}

/**
 * Sum of the out-of-base component minimum unit prices of a line (printing + cutting + lamination).
 * @param l The quote line.
 * @param cat The catalogue.
 * @returns The sum of the component minimum unit prices.
 */
export function componentsMinUnitPrice(l: QuoteLine, cat: Catalogue = ACTIVE_CATALOGUE): number {
  // Finishings: only CONF-1639 (lamination) carries one so far — inert on all the others.
  const finishings = [l.finishing1, l.finishing2].reduce((s, c) => s + (c ? componentMinUnitPriceOf(cat.finishings, c) : 0), 0);
  return componentMinUnitPriceOf(cat.prints, l.print) + componentMinUnitPriceOf(cat.cuts, l.cut) + componentMinUnitPriceOf(cat.laminations, l.lamination) + finishings;
}

// Component setup minimum flat fee (R-DP-1)

/** Component carrying a setup minimum flat fee (cutting today; extensible to print/finishing). */
interface WithFlatFee {
  code: string;
  min_flat_fee?: MinUnitPrice;
}

/**
 * Minimum flat fee of a component (global €, outside the base) — 0 when the field is empty/absent.
 * @param list The component list to search.
 * @param code The component code.
 * @returns The component flat fee, or 0.
 */
export function flatFeeComponentOf(list: readonly WithFlatFee[], code: string | undefined): number {
  const c = list.find((x) => x.code === code);
  return c && isFilled(c.min_flat_fee) ? num(c.min_flat_fee) : 0;
}

/**
 * Components of a line carrying a setup minimum flat fee, with their amount (R-DP-1). CUTTING (sheet
 * cuts CUT-1514→1519) and FINISHING (CONF-1639, lamination). A component without a flat fee yields
 * none. CONSOLIDATION "once per material and per distinct component" is done by the caller.
 * @param l The quote line.
 * @param cat The catalogue.
 * @returns The components carrying a flat fee, with their amount.
 */
export function componentFlatFees(l: QuoteLine, cat: Catalogue = ACTIVE_CATALOGUE): { code: string; fee: number }[] {
  const out: { code: string; fee: number }[] = [];
  const cutFee = flatFeeComponentOf(cat.cuts, l.cut);
  if (cutFee > 0 && l.cut) out.push({ code: l.cut, fee: cutFee });
  // Line finishings: each DISTINCT finishing carrying a flat fee triggers it once.
  for (const finishing of [l.finishing1, l.finishing2]) {
    if (!finishing || out.some((o) => o.code === finishing)) continue;
    const fee = flatFeeComponentOf(cat.finishings, finishing);
    if (fee > 0) out.push({ code: finishing, fee: fee });
  }
  return out;
}

/**
 * R-DP-1 consolidation of the component flat fees of ONE material: each DISTINCT component (by code)
 * counts its flat fee ONCE, however many lines use it (economic meaning: a fixed setup cost paid once
 * per job — symmetrical to the consolidated material flat fee). So 3 sheet-cut lines (€50) + 2 complex-cut
 * lines (€70) → 50 + 70, never 3×50 + 2×70. Returns the deduplicated detail (first-appearance order, for
 * the summary tooltip) and its total.
 * @param feesPerLine The component flat fees of every line of the material.
 * @returns The deduplicated detail and its total.
 */
export function consolidateComponentFlatFees(
  feesPerLine: readonly { code: string; fee: number }[],
): { detail: { code: string; fee: number }[]; total: number } {
  const detail: { code: string; fee: number }[] = [];
  for (const f of feesPerLine) {
    if (f.fee > 0 && !detail.some((x) => x.code === f.code)) detail.push({ code: f.code, fee: f.fee });
  }
  return { detail, total: detail.reduce((s, x) => s + x.fee, 0) };
}

/**
 * Material minimum unit price of the line (€/piece, base included). Priority order: line-forced
 * minimum → material admin override → catalogue minimum (cumulated with the laminated material).
 * @param l The quote line.
 * @param matForce The material's admin override, if any.
 * @param cat The catalogue.
 * @param clientMinUnitPrice The client minimum unit price (dedicated rate table), if any.
 * @returns The material minimum unit price.
 */
export function materialMinUnitPrice(
  l: QuoteLine,
  matForce?: ForcedMaterial,
  cat: Catalogue = ACTIVE_CATALOGUE,
  clientMinUnitPrice?: number,
): number {
  if (isFilled(l.forcedMinUnitPrice)) return num(l.forcedMinUnitPrice);
  // CLIENT minimum unit price (§19 dedicated rate table) substitutes the material CATALOGUE minimum,
  // and only applies when the target carries one in the catalogue (no catalogue minimum = no floor to
  // substitute). Line forcing still wins.
  const materialEntry = findMat(l.material, cat);
  const cRef = materialInFinishing(l, cat);
  const matC = cRef ? findMat(cRef, cat) : undefined;
  const catalogueMin = num(materialEntry?.min_unit_price) + (matC ? num(matC.min_unit_price) : 0);
  if (clientMinUnitPrice != null && catalogueMin > 0) return num(clientMinUnitPrice);
  if (matForce && isFilled(matForce.minUnitPrice)) return num(matForce.minUnitPrice);
  return catalogueMin;
}

/**
 * Line floor (€/piece) = material minimum unit price (base included) + Σ out-of-base component minima.
 * `clientMinUnitPrice` (dedicated rate table §19) replaces the MATERIAL term only — never the component minima.
 * @param l The quote line.
 * @param matForce The material's admin override, if any.
 * @param cat The catalogue.
 * @param clientMinUnitPrice The client minimum unit price (dedicated rate table), if any.
 * @returns The line floor.
 */
export function elementFloor(
  l: QuoteLine,
  matForce?: ForcedMaterial,
  cat: Catalogue = ACTIVE_CATALOGUE,
  clientMinUnitPrice?: number,
): number {
  return materialMinUnitPrice(l, matForce, cat, clientMinUnitPrice) + componentsMinUnitPrice(l, cat);
}

/**
 * CATALOGUE material minimum unit price (€/piece, base included) — IGNORES any line forcing
 * (`forcedMinUnitPrice`). Priority: material admin override → catalogue minimum (cumulated when laminated).
 * Feeds the always-visible minimum unit price column (auto, read-only): what the catalogue says,
 * regardless of what sales later forces as the final unit price.
 * @param l The quote line.
 * @param matForce The material's admin override, if any.
 * @param cat The catalogue.
 * @param clientMinUnitPrice The client minimum unit price (dedicated rate table), if any.
 * @returns The catalogue material minimum unit price.
 */
export function catalogueMaterialMinUnitPrice(
  l: QuoteLine,
  matForce?: ForcedMaterial,
  cat: Catalogue = ACTIVE_CATALOGUE,
  clientMinUnitPrice?: number,
): number {
  const materialEntry = findMat(l.material, cat);
  const cRef = materialInFinishing(l, cat);
  const matC = cRef ? findMat(cRef, cat) : undefined;
  const catalogueMin = num(materialEntry?.min_unit_price) + (matC ? num(matC.min_unit_price) : 0);
  // The CLIENT minimum is the applicable floor, shown in the minimum unit price column (red border
  // §21 extended: client price below the CLIENT minimum). Same guard as materialMinUnitPrice.
  if (clientMinUnitPrice != null && catalogueMin > 0) return num(clientMinUnitPrice);
  if (matForce && isFilled(matForce.minUnitPrice)) return num(matForce.minUnitPrice);
  return catalogueMin;
}

/**
 * CATALOGUE floor of the line (€/piece), line forcing excluded = catalogue material minimum unit
 * price (or CLIENT when a rate-table entry carries one) + Σ out-of-base component minima. Value shown
 * in the minimum unit price column (auto, read-only).
 * @param l The quote line.
 * @param matForce The material's admin override, if any.
 * @param cat The catalogue.
 * @param clientMinUnitPrice The client minimum unit price (dedicated rate table), if any.
 * @returns The catalogue floor of the line.
 */
export function catalogueFloor(
  l: QuoteLine,
  matForce?: ForcedMaterial,
  cat: Catalogue = ACTIVE_CATALOGUE,
  clientMinUnitPrice?: number,
): number {
  return catalogueMaterialMinUnitPrice(l, matForce, cat, clientMinUnitPrice) + componentsMinUnitPrice(l, cat);
}

/** Outcome of the line's unit price selection. */
export interface PuDef {
  /** Unit price kept (basis of the line total). */
  pu: number;
  /** true when the floor beats the theoretical unit price (highlighted cell). */
  floorApplied: boolean;
}

/**
 * Final unit price: the higher of the theoretical unit price (area × discounted m² price) and the
 * floor. A forced price (`forced`) bypasses the floor — forcing wins (book §3).
 * @param theoreticalUnitPrice The theoretical unit price.
 * @param floor The line floor.
 * @param forced Whether the price is forced by hand.
 * @returns The unit price kept and whether the floor applied.
 */
export function defaultUnitPrice(theoreticalUnitPrice: number, floor: number, forced: boolean): PuDef {
  if (forced) return { pu: theoreticalUnitPrice, floorApplied: false };
  return { pu: Math.max(theoreticalUnitPrice, floor), floorApplied: floor > theoreticalUnitPrice };
}

/**
 * Minimum flat fee of a line's material (global €), cumulated with the laminated material.
 * @param l The quote line.
 * @param cat The catalogue.
 * @returns The material flat fee.
 */
export function materialFlatFee(l: QuoteLine, cat: Catalogue = ACTIVE_CATALOGUE): number {
  const materialEntry = findMat(l.material, cat);
  const cRef = materialInFinishing(l, cat);
  const matC = cRef ? findMat(cRef, cat) : undefined;
  return num(materialEntry?.min_flat_fee) + (matC ? num(matC.min_flat_fee) : 0);
}

/**
 * Does the material minimum flat fee trigger? Yes when the material's cumulated total is strictly
 * positive and below the flat fee: the flat fee is then billed instead. It disappears as soon as the
 * cumulation reaches or exceeds the flat fee (book §3, dynamic volume discount).
 * @param totalMaterial The cumulated material total.
 * @param flatFee The material flat fee.
 * @returns True when the flat fee is billed instead of the material total.
 */
export function triggerFlatFee(totalMaterial: number, flatFee: number): boolean {
  return totalMaterial > 0 && totalMaterial < flatFee;
}
